import { findIdentityInForm } from "../blocks/forms/get_identity";
import ConfigStore from "../common/configstore";
import AutoTracker, { InitOptions } from "../common/tracker";
import { DEFAULT_UPLOAD_PATH, SYFT_VERSION } from "../common/types";
import { BatchUploader } from "../common/uploader";
import { getCurrentPath, ready } from "../common/utils";
import { buttonClicks } from "../plugins/buttonClicks";
import { formSubmits } from "../plugins/formSubmit";
import { linkClicks } from "../plugins/linkClicks";
import { pageViews } from "../plugins/pageViews";
import { InteractionTime } from "../plugins/sessionTrack";
import { getSourceTouch } from "../common/ad_utm";
import type { SourceTouch } from "../common/event_types";
import { isGPCEnabled, subscribeToConsent } from "./consent";

export type ExistingLog = [string, ...any[]];

export type OnTrackerReady = (sourceId: string, anonymousId: string) => void;

declare global {
  interface Window {
    syftc: SyftProps;
    syft:
      | AutoTracker<any>
      | { fetchID: (options?: any) => Promise<any>; _queue?: ExistingLog[] }
      | { enable: () => void }
      | DisabledSyft;
    posthog: any;
    hsq: any;
  }
}

interface StopOptions {
  flush?: boolean;
  clearStorage?: boolean;
}

type StopSyft = (_options?: StopOptions) => void;

export interface FetchIDCall {
  args: any[];
  resolve: (_value: any) => void;
  reject: (_reason?: any) => void;
}

interface DisabledSyft {
  enable: () => void;
  disable: () => void;
  identify: () => Promise<string[]>;
  track: () => void;
  page: () => void;
  signup: () => Promise<string[]>;
  fetchID: () => Promise<{ icps: null; error: string }>;
  reb2bLoaded?: boolean;
  // Only carried over while tracking is deferred, so that a later enable()
  // can still replay what the snippet queued before the bundle loaded.
  q?: ExistingLog[];
  fi?: FetchIDCall[];
}

const TRACKING_DISABLED_RESULT = {
  icps: null,
  error: "Tracking is disabled",
} as const;

export interface SyftProps {
  fetchIDPromises?: Array<{
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
    options?: any;
  }>;

  autoTrackEnabled?: boolean;
  trackPageViews?: boolean;
  hashMode?: boolean;

  trackOutboundLinks?: boolean;
  trackButtonClicks?: boolean;
  trackFormSubmits?: boolean;

  enabled?: boolean;

  /**
   * The path to upload the events to. Defaults to `/api/syft`.
   */
  uploadPath?: string;
  replicateTo?: string[];
  sourceId?: string;

  acrossDomain?: boolean;

  returnICPsOnIdentify?: boolean;
  enableRealTimeID?: boolean;
  realTimeIDTimeout?: number;
  icpSelector?: string;

  identifyFormPage?: string;
}

function isTagTestMode(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("syft_test") === "1" && window.opener !== null;
}

function sendTestResult(result: {
  success: boolean;
  apiKey?: string | null;
  version?: string | null;
  error?: string | null;
  rb2b?: boolean;
}): void {
  if (!isTagTestMode()) return;

  const message = {
    type: "syft_tag_test_result",
    success: result.success,
    apiKey: result.apiKey || null,
    version: result.version || null,
    error: result.error || null,
    timestamp: Date.now(),
    rb2b: result.rb2b,
  };

  const ALLOWED_ORIGINS = [
    "https://app.syftdata.com",
    "https://syftdev.vercel.app",
    "http://localhost:3000",
    "http://localhost:5173",
    "http://localhost:4173",
  ];

  if (window.opener && !window.opener.closed) {
    ALLOWED_ORIGINS.forEach((origin) => {
      try {
        window.opener.postMessage(message, origin);
      } catch (_e) {
        /* cross-origin errors are expected for non-matching origins */
      }
    });
  }
}

function getCookieDomain(props: SyftProps): string | undefined {
  if (props.acrossDomain === false) return undefined;

  const hostname = window.location.hostname;
  const isIpAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
  const parts = hostname.split(".");
  if (isIpAddress || parts.length <= 1) return undefined;

  if (parts.length > 2) parts.shift();
  return parts.join(".");
}

function clearSyftStorage(props: SyftProps): void {
  new ConfigStore("syft", getCookieDomain(props)).clear();
}

/**
 * Builds the no-op SDK installed while tracking is not running.
 *
 * @param retainQueue keep the snippet's pending `q`/`fi` entries so a later
 *   enable() can replay them. Only safe when tracking is deferred rather than
 *   denied — a denial must not hold on to queued personal data.
 */
function createDisabledSyft(
  enable: () => void,
  disable: () => void,
  retainQueue: boolean = false
): DisabledSyft {
  const existing = window.syft as
    | { reb2bLoaded?: boolean; q?: ExistingLog[]; fi?: FetchIDCall[] }
    | undefined;
  return {
    enable,
    disable,
    identify: async () => [],
    track: () => {},
    page: () => {},
    signup: async () => [],
    fetchID: async () => ({ ...TRACKING_DISABLED_RESULT }),
    reb2bLoaded: existing?.reb2bLoaded,
    ...(retainQueue ? { q: existing?.q, fi: existing?.fi } : {}),
  };
}

function resolveQueuedFetchIDCalls(): void {
  if (window.syft == null || typeof window.syft !== "object") return;
  if (!("fi" in window.syft) || !Array.isArray(window.syft.fi)) return;

  window.syft.fi.forEach(({ resolve }) => {
    resolve({ ...TRACKING_DISABLED_RESULT });
  });
  window.syft.fi.length = 0;
}

function startSyft(
  props: SyftProps,
  onTrackerReady?: OnTrackerReady,
  onDisable?: () => void,
  landingTouch?: SourceTouch
): StopSyft {
  props.enableRealTimeID =
    props.enableRealTimeID ||
    props.returnICPsOnIdentify ||
    props.icpSelector != null;
  props.realTimeIDTimeout = props.realTimeIDTimeout || 2000;

  const domain = getCookieDomain(props);
  const store = new ConfigStore("syft", domain);
  const deregisterCallbacks: Array<() => void> = [];
  let active = true;

  // pass the url based on the proxy options.
  const uploader = new BatchUploader(
    {
      sourceId: props.sourceId,
      url: props.uploadPath ?? DEFAULT_UPLOAD_PATH,
      replicateTo: props.replicateTo,
    },
    store
  );

  const tracker = new AutoTracker(
    {
      ...props,
      uploader,
      onDisable,
      landingTouch,
    } as InitOptions,
    store
  );

  const a = new InteractionTime({
    callback: {
      onNewSession: (session) => {
        if (!active) return;
        tracker.session = session;
        if (props.autoTrackEnabled !== false) {
          if (tracker.source?.syftIds?.email != null) {
            tracker.identify(tracker.source.syftIds.email, {
              source: "url_track",
            });
          }
        }
      },
      onEndSession: (_s) => {
        if (!active) return;
        tracker.session = undefined;
      },
      onContinueSession: (session, activeTime, sessionLength) => {
        if (!active) return;
        tracker.session = session;
        if (props.autoTrackEnabled !== false) {
          tracker.track("syft_session", {
            activeTime,
            sessionLength,
          });
        }
      },
      onPageHidden: () => {
        if (!active) return;
        uploader.flushOnPageHide();
      },
    },
    configStore: store,
  });
  deregisterCallbacks.push(a.destroy);
  if (props.autoTrackEnabled !== false) {
    if (props.trackPageViews !== false) {
      let currentPath: string | undefined;
      const callPage = (path: string): void => {
        if (!active) return;
        if (path == currentPath) return;
        currentPath = path;
        ready(() => {
          if (!active) return;
          if (currentPath !== path) return;
          tracker.page(undefined, path);
        }, 1000);
      };
      callPage(getCurrentPath(props.hashMode !== false));
      const cb = pageViews(callPage, props.hashMode !== false);
      deregisterCallbacks.push(cb);
    }

    if (props.trackOutboundLinks !== false) {
      const cb = linkClicks((href) => {
        if (!active) return;
        tracker.track("OutboundLink Clicked", { href });
      });
      deregisterCallbacks.push(cb);
    }
    if (props.trackButtonClicks !== false) {
      const cb = buttonClicks((type, text, selectors) => {
        if (!active) return;
        tracker.track("Button Clicked", { type, text, selectors });
      });
      deregisterCallbacks.push(cb);
    }
    if (props.trackFormSubmits !== false) {
      const cb = formSubmits(
        (path, formData, destination) => {
          if (!active) return;
          const eventName =
            destination != null &&
            destination.hostname !== window.location.hostname
              ? "Outbound Form"
              : "Form Submit";
          const identity = findIdentityInForm(formData.fields);
          tracker.track(eventName, { ...identity });
          if (
            props.identifyFormPage == null ||
            path === props.identifyFormPage
          ) {
            if (identity.id != null) {
              tracker.identify(identity.id, {
                ...identity.traits,
                source: "form_submit",
              });
            }
          }
        },
        (path, formData) => {
          if (!active) return;
          const identity = findIdentityInForm(formData.fields);
          if (
            props.identifyFormPage == null ||
            path === props.identifyFormPage
          ) {
            if (identity.id != null) {
              tracker.identify(identity.id, {
                ...identity.traits,
                source: "form_fill",
              });
            }
          }
        },
        tracker.source?.campaign
      );
      deregisterCallbacks.push(cb);
    }
  }

  let existingData: ExistingLog[] = [];
  let existingFetchIDPromises: {
    args: any[];
    resolve: (_v: any) => void;
    reject: (_reason?: any) => void;
  }[] = [];

  if (window.syft && typeof window.syft === "object") {
    if (Array.isArray(window.syft)) {
      existingData = window.syft;
    }
    if ("q" in window.syft && Array.isArray(window.syft.q)) {
      existingData = window.syft.q;
    }
    if ("fi" in window.syft && Array.isArray(window.syft.fi)) {
      existingFetchIDPromises = window.syft.fi;
    }
  }

  const reb2bLoaded = (window.syft as { reb2bLoaded?: boolean })?.reb2bLoaded;
  window.syft = tracker;
  if (reb2bLoaded === true) {
    (window.syft as AutoTracker<any> & { reb2bLoaded?: boolean }).reb2bLoaded =
      true;
  }

  if (onTrackerReady && props.sourceId) {
    onTrackerReady(props.sourceId, tracker.anonymousId);
  }

  existingData.forEach((data: any[]) => {
    const type = data.shift();
    if (type === "page") {
      tracker.page(...(data as [string, string?]));
    } else if (type === "track") {
      tracker.track(...(data as [string, Record<string, any>]));
    } else if (type === "signup") {
      tracker.signup(...(data as [string, Record<string, any>]));
    } else if (type === "identify") {
      tracker.identify(...(data as [string, Record<string, any>]));
    }
  });

  existingFetchIDPromises.forEach(({ args, resolve, reject }) => {
    tracker
      .fetchID(...args)
      .then(resolve)
      .catch(reject);
  });
  existingFetchIDPromises.length = 0;

  sendTestResult({
    success: true,
    apiKey: props.sourceId,
    version: SYFT_VERSION,
    rb2b:
      !!(window as any).reb2b &&
      ((window as any).reb2b.invoked === true ||
        (window as any).reb2b.loaded === true),
  });

  return ({ flush = true, clearStorage = false }: StopOptions = {}) => {
    if (!active) return;
    if (!flush) {
      active = false;
      tracker.dispose();
    }

    deregisterCallbacks.forEach((deregister) => {
      deregister();
    });
    deregisterCallbacks.length = 0;

    if (flush) {
      uploader.flushOnPageHide();
      active = false;
    } else {
      uploader.discard();
    }

    if (clearStorage) store.clear();
  };
}

let syftDeregister: StopSyft | undefined;
let consentDeregister: (() => void) | undefined;

function stopSyft(options?: StopOptions): void {
  const stop = syftDeregister;
  syftDeregister = undefined;
  stop?.(options);
}

function enableSyft(
  props: SyftProps,
  onTrackerReady?: OnTrackerReady,
  onDisable?: () => void,
  landingTouch?: SourceTouch
) {
  if (syftDeregister != null) return;

  syftDeregister = startSyft(props, onTrackerReady, onDisable, landingTouch);
}

function initSyft(onTrackerReady?: OnTrackerReady) {
  try {
    let props: SyftProps = {};
    const el = document.getElementById("syft-loader");
    if (el != null) {
      const apiKey = el?.getAttribute("data-api-key");
      const enabled = el?.getAttribute("data-enabled");
      const autoTrackEnabled = el?.getAttribute("data-auto-track-enabled");
      if (apiKey != null) {
        props.sourceId = apiKey;
      }
      if (enabled != null) {
        props.enabled = enabled === "true";
      }
      if (autoTrackEnabled != null) {
        props.autoTrackEnabled = autoTrackEnabled === "true";
      }
    }
    props = {
      ...props,
      ...window.syftc,
    };
    window.syftc = props;

    // Kept in memory only: nothing is stored until tracking is enabled, but
    // an enable() later on this page still sees the landing URL's click IDs.
    const landingTouch = getSourceTouch();

    const explicitEnable = () => {
      if (isGPCEnabled()) {
        disableTracking();
        return;
      }
      props.enabled = true;
      window.syftc.enabled = true;
      enableSyft(props, onTrackerReady, disableTracking, landingTouch);
    };

    /**
     * A consent decision went against us: GPC, a TCF withdrawal, or an
     * explicit disable() call. Tear down anything running and erase
     * Syft-owned state.
     */
    const disableTracking = () => {
      props.enabled = false;
      window.syftc.enabled = false;
      stopSyft({ flush: false, clearStorage: true });
      clearSyftStorage(props);
      resolveQueuedFetchIDCalls();
      window.syft = createDisabledSyft(explicitEnable, disableTracking);
    };

    /**
     * Tracking has not been switched on yet — the integrator gates it behind
     * their own consent flow and will call enable() later. This is not a
     * consent decision, so existing identifiers, attribution and queued calls
     * are left untouched; clearing them here would hand every visitor a new
     * anonymousId on every page load.
     */
    const deferTracking = () => {
      window.syft = createDisabledSyft(explicitEnable, disableTracking, true);
    };

    // pagehide fires reliably on mobile and on tab close, unlike beforeunload.
    window.addEventListener("pagehide", (event: PageTransitionEvent) => {
      // A page entering the back/forward cache may be restored, so keep the
      // SDK running; the session tracker has already flushed on hide.
      if (event.persisted) return;
      consentDeregister?.();
      consentDeregister = undefined;
      stopSyft({ flush: true });
    });

    if (isGPCEnabled()) {
      disableTracking();
      return;
    }

    if (props.enabled === false) {
      deferTracking();
      return;
    }

    if (props.enabled === true) {
      enableSyft(props, onTrackerReady, disableTracking, landingTouch);
      return;
    }

    consentDeregister = subscribeToConsent((consentGiven: boolean) => {
      if (consentGiven) {
        explicitEnable();
      } else {
        disableTracking();
      }
    });
  } catch (e) {
    console.warn("Syft failed to load", e);
    sendTestResult({
      success: false,
      error: e instanceof Error ? e.message : String(e),
    });
  }
}

/**
 * Boots the Syft SDK by listening for DOM ready events and calling initSyft.
 * @param onTrackerReady Optional callback invoked after the tracker is created,
 *   receiving (sourceId, anonymousId). Used by variant bundles (e.g. RB2B).
 */
export function bootSyft(onTrackerReady?: OnTrackerReady): void {
  let initRan = false;
  const init = () => {
    if (initRan) return;
    initRan = true;
    initSyft(onTrackerReady);
  };

  if (document.readyState === "complete") {
    init();
  }
  window.addEventListener("load", () => {
    init();
  });
  document.addEventListener("DOMContentLoaded", () => {
    init();
  });
}
